/** The render quality tiers (quality.ts documents what they trade; QualityGovernor owns the current one). */
export interface QualityTier {
  readonly name: string;
  /** Upper bound on the render pixel ratio (the device's own ratio still caps it). */
  readonly pixelRatio: number;
  readonly ao: boolean;
  readonly shadows: boolean;
  readonly softShadows: boolean;
  readonly shadowSize: number;
  /** Bloom render scale relative to the canvas: 0 is off. At 1 its bright pass runs at half the canvas per axis. */
  readonly bloom: number;
  /**
   * MSAA samples on the scene's render target. The scene always renders into the post chain's target, never the
   * canvas, so this is the only antialiasing there is. Off below ultra: the scene is several million triangles, most
   * of them smaller than a pixel (foliage), and multisampling shades a pixel once per triangle touching any of its
   * samples rather than once — on sub-pixel geometry that multiplies the fragment work, and it measured as the
   * single largest cost in the headless renderer. The phone's "high" never had it either: the canvas was
   * multisampled, but the scene went through the post chain's single-sampled target and only the final
   * full-screen quad reached the canvas.
   */
  readonly msaa: number;
  /**
   * FXAA folded into the post chain's final pass (postfx.ts), for tiers without MSAA: a handful of extra taps in a pass
   * the frame already pays for. The canvas itself is never multisampled (scene.ts), so a tier with neither has no AA.
   */
  readonly fxaa: boolean;
  /**
   * The ground's normal maps sampled at two scales and angles (groundShader.ts `dual`), as its albedo always is, to
   * break up their repeat. Only on ultra: the ground's shader covers most of the screen, and under the albedo's own
   * pair of samples one normal sample reads the same at a phone's view distance (RC1 perf; captures in docs/PERF.md).
   */
  readonly groundNormalDual: boolean;
}

export const QUALITY_TIERS: readonly QualityTier[] = [
  { name: "ultra", pixelRatio: 2, ao: true, shadows: true, softShadows: true, shadowSize: 2048, bloom: 1, msaa: 4, fxaa: false, groundNormalDual: true },
  { name: "high", pixelRatio: 1.5, ao: false, shadows: true, softShadows: true, shadowSize: 2048, bloom: 1, msaa: 0, fxaa: true, groundNormalDual: false },
  { name: "medium", pixelRatio: 1.25, ao: false, shadows: true, softShadows: false, shadowSize: 1024, bloom: 0.5, msaa: 0, fxaa: true, groundNormalDual: false },
  { name: "low", pixelRatio: 1, ao: false, shadows: true, softShadows: false, shadowSize: 1024, bloom: 0, msaa: 0, fxaa: true, groundNormalDual: false },
  { name: "lowest", pixelRatio: 0.8, ao: false, shadows: false, softShadows: false, shadowSize: 512, bloom: 0, msaa: 0, fxaa: false, groundNormalDual: false },
];

/** Index of a tier by name, or -1. */
export function tierIndex(name: string): number { return QUALITY_TIERS.findIndex((t) => t.name === name); }

/** Where a device starts with nothing saved: phones and tablets at `high` (AO at 2x on a phone is the single biggest cost), desktops at `ultra`. */
export function defaultTier(): number {
  const touch = typeof matchMedia === "function" && matchMedia("(pointer: coarse)").matches;
  return touch ? tierIndex("high") : 0;
}

/**
 * `?q=high` (any tier name) pins the tier for the session: the governor starts locked there and never steps, and
 * nothing is saved. For captures: a software-GL capture renders at a few frames a second, which the governor rightly
 * reads as a slow device and steps down from.
 */
export function pinnedTier(search: string = typeof location === "object" && location ? location.search : ""): number | null {
  const m = /[?&]q=([a-z-]+)/.exec(search);
  if (!m) return null;
  const i = tierIndex(m[1]);
  return i >= 0 ? i : null;
}

/**
 * What the post chain must look like for a tier. The chain always exists (scene.ts renders every frame through it:
 * the governor's render scale and the canvas without MSAA depend on it); what varies is its MSAA, its FXAA and its
 * bloom scale (0 = bloom off, also under reduced effects). MSAA is fixed when a PostFx is built (the scene target's
 * sample count is set at allocation), so a tier change that changes it must rebuild — `postNeedsRebuild`. FXAA is a
 * define in the final pass and switches in place.
 */
export interface PostPlan { readonly msaa: number; readonly fxaa: boolean; readonly bloomScale: number }
export function postPlan(t: QualityTier, reducedVfx: boolean): PostPlan {
  return { msaa: t.msaa, fxaa: t.fxaa && t.msaa === 0 && !fxaaVetoed, bloomScale: reducedVfx ? 0 : t.bloom };
}
/**
 * FXAA is off on phones: it is ~9 extra full-screen taps a pixel for an edge softening the 1.5x tier already mostly
 * hides, and on an iPhone 15 Pro the tier with it (low) drew a black screen while the tier without (lowest) drew the
 * scene — the final pass did not survive its compile there. SceneRig.shaderHealth also vetoes it at runtime when the
 * final pass reports a failed program.
 */
export let fxaaVetoed = typeof matchMedia === "function" && matchMedia("(pointer: coarse)").matches;
export function vetoFxaa(): void { fxaaVetoed = true; }
/** True when the PostFx that exists (`built`, null for none) cannot serve `plan` and must be (re)built. */
export function postNeedsRebuild(built: { msaa: number } | null, plan: PostPlan): boolean {
  return built === null || built.msaa !== plan.msaa;
}

/**
 * The five-step band (0 ultra … 4 lowest) the VFX particle budget, pollen and detail density are tuned on. The tier
 * list is those five today, so this is the identity by name; it stays the one place to map a tier onto a band if the
 * list grows again (an unknown name reads as `low`).
 */
export function tierBand(t: QualityTier): { index: number; name: "ultra" | "high" | "medium" | "low" | "lowest" } {
  const names = ["ultra", "high", "medium", "low", "lowest"] as const;
  const i = names.indexOf(t.name as (typeof names)[number]);
  const idx = i >= 0 ? i : 3;
  return { index: idx, name: names[idx] };
}

/** True when two tiers configure the renderer identically (every field of QualityTier equal). */
export function sameTier(a: QualityTier, b: QualityTier): boolean {
  for (const k of Object.keys(a) as (keyof QualityTier)[]) if (a[k] !== b[k]) return false;
  return Object.keys(b).length === Object.keys(a).length;
}
